<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Let's Encrypt cho Nginx</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Cài đặt SSL miễn phí: Hướng dẫn Let's Encrypt + Certbot cho Nginx 2025</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>—</span>
          <span class="mx-2">•</span>
          <span>8 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Bảo mật website</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>Let's Encrypt là gì và tại sao nên sử dụng?</h2>
          <p>Let's Encrypt là tổ chức phi lợi nhuận cung cấp chứng chỉ SSL/TLS miễn phí cho mọi website. Được hỗ trợ bởi các công ty lớn như Mozilla, Google, Facebook.</p>
          
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4 text-green-800">✅ Lợi ích của Let's Encrypt</h3>
            <ul class="space-y-2 text-green-700">
              <li>• <strong>Hoàn toàn miễn phí:</strong> Không tốn chi phí hàng năm</li>
              <li>• <strong>Tự động gia hạn:</strong> Chứng chỉ tự động gia hạn mỗi 90 ngày</li>
              <li>• <strong>Bảo mật cao:</strong> Sử dụng thuật toán mã hóa mạnh nhất</li>
              <li>• <strong>Dễ cài đặt:</strong> Chỉ cần vài lệnh đơn giản</li>
              <li>• <strong>Hỗ trợ wildcard:</strong> Chứng chỉ cho subdomain</li>
            </ul>
          </div>

          <h2>1. Chuẩn bị môi trường</h2>
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-blue-800">📋 Yêu cầu hệ thống</h3>
            <ul class="space-y-2 text-blue-700">
              <li>• <strong>Server:</strong> Ubuntu 18.04+ hoặc CentOS 7+</li>
              <li>• <strong>Domain:</strong> Đã trỏ A record về IP server</li>
              <li>• <strong>Nginx:</strong> Đã cài đặt và chạy trên port 80</li>
              <li>• <strong>Firewall:</strong> Mở port 80 và 443</li>
              <li>• <strong>Root access:</strong> Quyền sudo hoặc root</li>
            </ul>
          </div>

          <h3>Kiểm tra domain và Nginx:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Kiểm tra domain trỏ đúng IP
nslookup yourdomain.com
dig yourdomain.com

# Kiểm tra Nginx đang chạy
sudo systemctl status nginx
sudo nginx -t

# Kiểm tra port 80
sudo netstat -tlnp | grep :80</code></pre>
          </div>

          <h2>2. Cài đặt Certbot</h2>
          <p>Certbot là công cụ chính thức để lấy chứng chỉ từ Let's Encrypt. Hỗ trợ nhiều web server khác nhau.</p>

          <h3>Ubuntu/Debian:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Cập nhật package list
sudo apt update

# Cài đặt Certbot và Nginx plugin
sudo apt install -y certbot python3-certbot-nginx

# Kiểm tra phiên bản
certbot --version</code></pre>
          </div>

          <h3>CentOS/RHEL:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Cài đặt EPEL repository
sudo yum install -y epel-release

# Cài đặt Certbot
sudo yum install -y certbot python3-certbot-nginx

# Hoặc sử dụng snap (khuyến nghị)
sudo snap install --classic certbot</code></pre>
          </div>

          <h3>Snap Installation (Khuyến nghị):</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Cài đặt snapd
sudo apt install -y snapd

# Cài đặt Certbot qua snap
sudo snap install --classic certbot

# Tạo symlink
sudo ln -s /snap/bin/certbot /usr/bin/certbot</code></pre>
          </div>

          <h2>3. Cấu hình Nginx cơ bản</h2>
          <p>Trước khi lấy chứng chỉ, cần cấu hình Nginx để phục vụ website trên HTTP.</p>

          <h3>Tạo virtual host:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tạo file cấu hình cho domain
sudo nano /etc/nginx/sites-available/yourdomain.com

# Nội dung file cấu hình
server {
    listen 80;
    server_name yourdomain.com www.yourdomain.com;
    
    root /var/www/yourdomain.com;
    index index.html index.htm;
    
    location / {
        try_files $uri $uri/ =404;
    }
}</code></pre>
          </div>

          <h3>Kích hoạt site:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tạo symlink
sudo ln -s /etc/nginx/sites-available/yourdomain.com /etc/nginx/sites-enabled/

# Test cấu hình
sudo nginx -t

# Reload Nginx
sudo systemctl reload nginx</code></pre>
          </div>

          <h2>4. Lấy chứng chỉ SSL</h2>
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-green-800">🔐 Các phương pháp xác thực</h3>
            <div class="grid md:grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-green-600">HTTP-01 Challenge</h4>
                <p class="text-sm text-gray-600">Tạo file tạm thời trên web server</p>
                <p class="text-xs text-gray-500">Phù hợp: Web server, dễ cài đặt</p>
              </div>
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-blue-600">DNS-01 Challenge</h4>
                <p class="text-sm text-gray-600">Thêm TXT record vào DNS</p>
                <p class="text-xs text-gray-500">Phù hợp: Wildcard, server không public</p>
              </div>
            </div>
          </div>

          <h3>Lệnh cơ bản (HTTP-01):</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Lấy chứng chỉ cho domain đơn
sudo certbot --nginx -d yourdomain.com

# Lấy chứng chỉ cho nhiều domain
sudo certbot --nginx -d yourdomain.com -d www.yourdomain.com -d api.yourdomain.com

# Lấy chứng chỉ wildcard (cần DNS-01)
sudo certbot certonly --manual --preferred-challenges dns -d "*.yourdomain.com" -d yourdomain.com</code></pre>
          </div>

          <h3>Quy trình tương tác:</h3>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <ol class="space-y-3">
              <li><strong>Email:</strong> Nhập email để nhận thông báo gia hạn</li>
              <li><strong>Terms:</strong> Đồng ý với điều khoản sử dụng</li>
              <li><strong>Sharing:</strong> Chọn có/không chia sẻ email với EFF</li>
              <li><strong>Domain:</strong> Xác nhận domain cần lấy chứng chỉ</li>
              <li><strong>Redirect:</strong> Chọn có redirect HTTP → HTTPS</li>
            </ol>
          </div>

          <h2>5. Cấu hình Nginx với SSL</h2>
          <p>Sau khi lấy chứng chỉ, Certbot sẽ tự động cấu hình Nginx. Nhưng bạn nên tùy chỉnh để tối ưu bảo mật.</p>

          <h3>Cấu hình SSL tối ưu:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code>server {
    listen 443 ssl http2;
    server_name yourdomain.com www.yourdomain.com;
    
    # SSL Configuration
    ssl_certificate /etc/letsencrypt/live/yourdomain.com/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/yourdomain.com/privkey.pem;
    
    # SSL Security
    ssl_protocols TLSv1.2 TLSv1.3;
    ssl_ciphers ECDHE-RSA-AES256-GCM-SHA512:DHE-RSA-AES256-GCM-SHA512:ECDHE-RSA-AES256-GCM-SHA384;
    ssl_prefer_server_ciphers off;
    ssl_session_cache shared:SSL:10m;
    ssl_session_timeout 10m;
    
    # HSTS
    add_header Strict-Transport-Security "max-age=31536000; includeSubDomains" always;
    
    # Security Headers
    add_header X-Frame-Options DENY;
    add_header X-Content-Type-Options nosniff;
    add_header X-XSS-Protection "1; mode=block";
    
    root /var/www/yourdomain.com;
    index index.html;
    
    location / {
        try_files $uri $uri/ =404;
    }
}

# Redirect HTTP to HTTPS
server {
    listen 80;
    server_name yourdomain.com www.yourdomain.com;
    return 301 https://$server_name$request_uri;
}</code></pre>
          </div>

          <h2>6. Gia hạn tự động</h2>
          <p>Chứng chỉ Let's Encrypt có thời hạn 90 ngày. Cần thiết lập gia hạn tự động.</p>

          <h3>Kiểm tra timer hiện tại:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Kiểm tra timer gia hạn
sudo systemctl list-timers | grep certbot

# Kiểm tra cron job
sudo crontab -l

# Test gia hạn (dry run)
sudo certbot renew --dry-run</code></pre>
          </div>

          <h3>Thiết lập gia hạn tự động:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tạo script gia hạn
sudo nano /etc/cron.d/certbot

# Nội dung file
0 12 * * * root certbot renew --quiet --post-hook "systemctl reload nginx"

# Hoặc sử dụng systemd timer
sudo systemctl enable certbot.timer
sudo systemctl start certbot.timer</code></pre>
          </div>

          <h2>7. Wildcard Certificate</h2>
          <p>Chứng chỉ wildcard cho phép bảo mật tất cả subdomain với một chứng chỉ duy nhất.</p>

          <h3>Cài đặt wildcard:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Lấy wildcard certificate
sudo certbot certonly --manual --preferred-challenges dns \
  -d "*.yourdomain.com" -d yourdomain.com

# Certbot sẽ yêu cầu thêm TXT record
# Thêm TXT record vào DNS zone
# Sau đó nhấn Enter để tiếp tục</code></pre>
          </div>

          <h3>Cấu hình Nginx cho wildcard:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code>server {
    listen 443 ssl http2;
    server_name *.yourdomain.com;
    
    ssl_certificate /etc/letsencrypt/live/yourdomain.com/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/yourdomain.com/privkey.pem;
    
    # Cấu hình SSL tương tự như trên
}</code></pre>
          </div>

          <h2>8. Troubleshooting</h2>
          <div class="bg-yellow-50 p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-yellow-800">⚠️ Các vấn đề thường gặp</h3>
            <div class="space-y-4">
              <div>
                <h4 class="font-medium">Rate limit exceeded:</h4>
                <ul class="text-sm space-y-1 ml-4">
                  <li>• Chờ 1 giờ trước khi thử lại</li>
                  <li>• Sử dụng staging environment để test</li>
                  <li>• Kiểm tra domain có bị block không</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">Domain validation failed:</h4>
                <ul class="text-sm space-y-1 ml-4">
                  <li>• Kiểm tra domain trỏ đúng IP</li>
                  <li>• Đảm bảo port 80 mở</li>
                  <li>• Kiểm tra firewall không chặn</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">Certificate not auto-renewing:</h4>
                <ul class="text-sm space-y-1 ml-4">
                  <li>• Kiểm tra cron job hoạt động</li>
                  <li>• Test renew command thủ công</li>
                  <li>• Kiểm tra log files</li>
                </ul>
              </div>
            </div>
          </div>

          <h2>9. Monitoring và Maintenance</h2>
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-blue-800">📊 Theo dõi chứng chỉ</h3>
            <ul class="space-y-2 text-blue-700">
              <li>• <strong>Check expiry:</strong> <code>sudo certbot certificates</code></li>
              <li>• <strong>Monitor logs:</strong> <code>sudo tail -f /var/log/letsencrypt/letsencrypt.log</code></li>
              <li>• <strong>Email alerts:</strong> Cấu hình email nhận thông báo</li>
              <li>• <strong>Automated monitoring:</strong> Sử dụng tools như Certbot Monitor</li>
            </ul>
          </div>

          <h2>10. Best Practices</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4">💡 Lời khuyên bảo mật</h3>
            <ul class="space-y-2 text-gray-700">
              <li>• <strong>Staging first:</strong> Test với <code>--staging</code> trước</li>
              <li>• <strong>Backup certificates:</strong> Backup định kỳ</li>
              <li>• <strong>Monitor expiry:</strong> Theo dõi ngày hết hạn</li>
              <li>• <strong>Security headers:</strong> Thêm security headers</li>
              <li>• <strong>Regular updates:</strong> Cập nhật Certbot thường xuyên</li>
            </ul>
          </div>

          <div class="bg-green-50 p-6 rounded-lg mt-8">
            <h3 class="font-semibold mb-3 text-green-800">🎉 Kết quả</h3>
            <p class="text-sm text-green-700">Sau khi hoàn thành, website của bạn sẽ có SSL certificate miễn phí, tự động gia hạn và đạt chuẩn bảo mật cao. Điều này giúp tăng trust score và cải thiện SEO ranking.</p>
          </div>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
const title = "Cài đặt SSL miễn phí: Hướng dẫn Let's Encrypt + Certbot cho Nginx 2025"
const desc  = "Hướng dẫn chi tiết cài đặt SSL miễn phí với Let's Encrypt và Certbot cho Nginx. Tự động gia hạn, wildcard certificate và bảo mật tối ưu"
const url   = 'https://webservices.vn/blog/lets-encrypt-certbot-nginx-2025'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
